import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import {
  Button, Icon, Layout, Table, Tooltip
} from 'antd';
import _ from 'lodash';
import settings from 'settings';
import uniqueId from 'components/common/uniqueId';
import HeaderBar from '../headerBar/header-bar';
import ToolBar from '../toolBar/tool-bar';

const DEFAULT_TABLE_PAGE_SIZE = settings.APP_CONFIG.defaultTablePageSize;

const { Content, Sider } = Layout;

const showTotalItems = (total, range) => `${range[0]} - ${range[1]} of ${total} items`;

class ExtendedTable extends React.Component {
  static propTypes = {
    title: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    className: PropTypes.string,
    showHeader: PropTypes.bool,
    showTitle: PropTypes.bool,
    showColumnHeader: PropTypes.bool,
    showPagination: PropTypes.bool,
    toolbar: PropTypes.element,
    commands: PropTypes.object,

    dataSource: PropTypes.array.isRequired,
    columns: PropTypes.array.isRequired,
    selectedRowKeys: PropTypes.array,

    rowKey: PropTypes.any.isRequired,
    rowSelection: PropTypes.object,
    rowSelectionConfig: PropTypes.object,
    scroll: PropTypes.object,

    page: PropTypes.number,
    pageSize: PropTypes.number,
    totalRecords: PropTypes.number,

    onCommandAddClick: PropTypes.func,
    onCommandDeleteClick: PropTypes.func,
    onCommandRefreshClick: PropTypes.func,

    onRowClick: PropTypes.func,
    onRowDoubleClick: PropTypes.func,
    onTableChange: PropTypes.func,

    showFilter: PropTypes.bool,
    customFilter: PropTypes.object,
    loading: PropTypes.bool,

    expandedRowRender: PropTypes.func
  };

  static defaultProps = {
    title: '',
    className: null,
    showHeader: true,
    showTitle: true,
    showColumnHeader: true,
    showPagination: true,
    toolbar: null,
    commands: {
      showAdd: true,
      showDelete: true,
      showRefresh: true
    },

    selectedRowKeys: [],

    rowSelection: null,
    rowSelectionConfig: {
      selectItemOnSingleClick: true,
      selectItemOnDoubleClick: true,
      highlightRow: true,
      multiRow: false,
      toggleRow: false
    },
    scroll: { x: true },

    page: 1,
    pageSize: DEFAULT_TABLE_PAGE_SIZE,
    totalRecords: 0,

    onCommandAddClick() {
    },
    onCommandDeleteClick() {
    },
    onCommandRefreshClick() {
    },

    onRowClick() {
    },
    onRowDoubleClick() {
    },
    onTableChange() {
    },

    showFilter: false,
    customFilter: {},
    loading: false,

    expandedRowRender: null
  };

  constructor(props) {
    super(props);

    const {
      selectedRowKeys, commands, page, pageSize, totalRecords, rowSelectionConfig
    } = this.props;

    this.state = {
      collapsed: true,
      selectedRowKeys,

      commands: {
        showAdd: true,
        showDelete: true,
        showRefresh: true,
        ...commands
      },

      pagination: {
        current: page,
        pageSize,
        total: totalRecords,
        showTotal: showTotalItems
      },

      rowSelectionConfig: {
        selectItemOnSingleClick: true,
        selectItemOnDoubleClick: true,
        highlightRow: true,
        multiRow: false,
        toggleRow: false,
        ...rowSelectionConfig
      }
    };

    this.handleCommandAddClick = this.handleCommandAddClick.bind(this);
    this.handleCommandDeleteClick = this.handleCommandDeleteClick.bind(this);
    this.handleCommandRefreshClick = this.handleCommandRefreshClick.bind(this);

    this.handleRowClick = this.handleRowClick.bind(this);
    this.handleRowDoubleClick = this.handleRowDoubleClick.bind(this);
    this.handleTableChange = this.handleTableChange.bind(this);

    this.handleToggleSider = this.handleToggleSider.bind(this);
  }

  handleTableChange = (pagination, filters, sorter) => {
    const { pagination: statePagination } = this.state;
    const { onTableChange } = this.props;
    // -- Update the table pagination to reflect
    // -- the current selected page and pageSize
    // ---
    const pager = {
      ...statePagination,
      current: pagination.current,
      pageSize: pagination.pageSize
    };

    this.setState({ pagination: pager });
    onTableChange(pagination, filters, sorter);
  };

  UNSAFE_componentWillReceiveProps(nextProps) {
    const { selectedRowKeys, pagination } = this.state;

    // -- Ensure the selectedRowKeys matches with the selected
    // -- row key from the list. If not, reselect the user.
    // ---
    const selectedRowKeysChanged = !_.isEqual(selectedRowKeys, nextProps.selectedRowKeys);
    if (selectedRowKeysChanged) {
      this.setState({
        selectedRowKeys: nextProps.selectedRowKeys
      });
    }

    // -- Reset the table pagination to reflect
    // -- the totalItems.
    // ---
    const pageChanged = pagination.current !== nextProps.page;
    const pageSizeChanged = pagination.pageSize !== nextProps.pageSize;
    const totalItemsChanged = pagination.total !== nextProps.totalRecords;

    if (pageChanged || pageSizeChanged || totalItemsChanged) {
      this.setState({
        pagination: {
          ...pagination,
          current: nextProps.page,
          pageSize: nextProps.pageSize,
          total: nextProps.totalRecords
        }
      });
    }
  }

  handleCommandAddClick() {
    const { onCommandAddClick } = this.props;
    onCommandAddClick();
  }

  handleCommandDeleteClick(itemKeys) {
    const { onCommandDeleteClick } = this.props;
    onCommandDeleteClick(itemKeys);
  }

  handleCommandRefreshClick() {
    const { onCommandRefreshClick } = this.props;
    onCommandRefreshClick();
  }

  handleRowClick(record) {
    const { rowSelectionConfig } = this.state;
    const { onRowClick } = this.props;
    if (rowSelectionConfig.selectItemOnSingleClick) {
      this.selectRow(record);
    }
    onRowClick(record);
  }

  handleRowDoubleClick(record) {
    const { rowSelectionConfig } = this.state;
    const { onRowDoubleClick } = this.props;
    if (rowSelectionConfig.selectItemOnDoubleClick) {
      this.selectRow(record);
    }
    onRowDoubleClick(record);
  }

  handleToggleSider() {
    const { collapsed } = this.state;
    this.setState({ collapsed: !collapsed });
  }

  selectRow(record) {
    const { rowSelectionConfig, selectedRowKeys } = this.state;
    const { rowKey } = this.props;
    const key = record[rowKey];
    const keyIndex = selectedRowKeys.indexOf(key);
    let newSelectedRowKeys = null;

    // -- See below URL for further samples on row selection
    // -- https://codepen.io/anon/pen/NdLYyg?editors=0110
    // -- https://codesandbox.io/s/000vqw38rl
    // ---
    if (rowSelectionConfig.multiRow) {
      // -- Multi Row Selection
      // ---
      newSelectedRowKeys = [...selectedRowKeys];
      if (keyIndex >= 0) {
        // -- If item exists in array and toggle is enabled, remove
        // -- the item from the array otherwise return original array
        // ---
        if (rowSelectionConfig.toggleRow) {
          newSelectedRowKeys.splice(keyIndex, 1);
        }
      }
      else {
        newSelectedRowKeys.push(key);
      }
    }
    // -- Single Row Selection
    // ---
    else if (keyIndex >= 0) {
      // -- If item exists in array and toggle is enabled, remove
      // -- the item from the array otherwise return original array
      // ---
      newSelectedRowKeys = rowSelectionConfig.toggleRow ? [] : [...selectedRowKeys];
    }
    else {
      newSelectedRowKeys = [key];
    }
    this.setState({ selectedRowKeys: newSelectedRowKeys });
  }

  renderToolbar(loading, commands, selectedRowKeys, toolbar) {
    const canDelete = selectedRowKeys.length > 0;

    return (
      <ToolBar>
        {toolbar}
        {(commands.showAdd || commands.showDelete || commands.showRefresh) && (
          <ToolBar.Right>
            {commands.showAdd && (
              <Tooltip title="New Item" placement="bottomRight">
                <Button className="addButton" shape="circle" size="small" icon="file-add" disabled={loading} onClick={this.handleCommandAddClick} />
              </Tooltip>
            )}
            {commands.showDelete && (
              <Tooltip title="Delete Item" placement="bottomRight">
                <Button
                  className="deleteButton"
                  shape="circle"
                  size="small"
                  icon="delete"
                  disabled={loading || !canDelete}
                  onClick={() => this.handleCommandDeleteClick(selectedRowKeys)}
                />
              </Tooltip>
            )}
            {commands.showRefresh && (
              <Tooltip title="Refresh" placement="bottomRight">
                <Button className="refreshButton" shape="circle" size="small" icon="reload" loading={loading} onClick={this.handleCommandRefreshClick} />
              </Tooltip>
            )}
          </ToolBar.Right>
        )}
      </ToolBar>
    );
  }

  render() {
    const {
      showFilter,
      customFilter,
      title,
      className,
      showHeader,
      showTitle,
      showColumnHeader,
      showPagination,
      toolbar,
      columns,
      rowKey,
      rowSelection,
      scroll,
      loading,
      expandedRowRender,
      dataSource,
    } = this.props;
    const {
      collapsed, commands, selectedRowKeys, rowSelectionConfig, pagination
    } = this.state;

    return (
      <div id={`ExtendedTable-${uniqueId(title)}`} className={className}>
        <Layout>
          {showFilter && (
            <Sider theme="light" width={280} trigger={null} collapsedWidth={0} collapsible collapsed={collapsed}>
              {customFilter}
            </Sider>
          )}
          <Content>
            {showHeader && (
              <HeaderBar
                title={title}
                showFilter={showFilter}
                showTitle={showTitle}
                toolbar={this.renderToolbar(loading, commands, selectedRowKeys, toolbar)}
                onToggleSider={this.handleToggleSider}
                showSider={!collapsed}
              />
            )}
            <Table
              expandRowByClick={false}
              expandedRowRender={expandedRowRender}
              loading={{
                spinning: loading,
                indicator: <Icon type="loading" spin />,
                tip: 'Loading...'
              }}
              className={classNames('bhp-table', { 'bhp-show-header': showHeader }, { 'bhp-show-pagination': showPagination })}
              showHeader={showColumnHeader}
              pagination={showPagination ? pagination : false}
              size="small"
              dataSource={dataSource}
              columns={columns}
              rowKey={rowKey}
              rowSelection={rowSelection}
              rowClassName={record => (rowSelectionConfig.highlightRow && selectedRowKeys.indexOf(record[rowKey]) >= 0 ? 'ant-table-row-selected' : '')}
              scroll={scroll}
              onRow={record => ({
                onClick: () => {
                  this.handleRowClick(record);
                },
                onDoubleClick: () => {
                  this.handleRowDoubleClick(record);
                }
              })}
              onChange={this.handleTableChange}
            />
          </Content>
        </Layout>
      </div>
    );
  }
}

export default ExtendedTable;
